import { useCallback } from "react";
import { toast } from "react-toastify";
import { NotificationList } from "~/components/common/notifications/settings/NotificationList";
import { NotificationModal } from "~/components/common/notifications/settings/NotificationModal";
import { useLineUserAccounts } from "~/hooks/vendors/useLineUserAccounts";
import { useSessions } from "~/hooks/vendors/useSessions";
import { useStatuses } from "~/hooks/vendors/useStatuses";
import { useVendorNotifications } from "~/hooks/vendors/useVendorNotifications";
import type { NskResponse } from "~/models/Api";
import type { Notification, NotificationEdit } from "~/models/Notification";
import type { Status } from "~/models/Status";
import { getWithVendorStatuses } from "~/models/Status";
import { isSuccess } from "~/utils/api/api_handler";
import {
	create,
	destroy,
	update
} from "~/utils/api/vendors/VendorNotification";

type Props = {
	isModalOpen: boolean;
	onModalClose: () => void;
};

export const NotificationSetting: React.FC<Props> = ({
	isModalOpen,
	onModalClose
}: Props) => {
	const { data: notifications, mutate: mutateNotifications } =
		useVendorNotifications().index();
	const { data: statuses } = useStatuses().index();
	const { data: session } = useSessions().index();
	const { data: lineUserAccounts } = useLineUserAccounts().index();

	const handleCreate = useCallback(
		async (notification: NotificationEdit): Promise<NskResponse> =>
			create(notification),
		[]
	);
	const handleUpdate = useCallback(
		async (
			notification: NotificationEdit,
			notificationId: number
		): Promise<NskResponse> => update(notification, notificationId),
		[]
	);
	const handleDestroy = useCallback(
		async (notification: Notification): Promise<NskResponse> =>
			destroy(notification),
		[]
	);
	const handleMutate = useCallback(async () => {
		await mutateNotifications();
	}, [mutateNotifications]);
	const handleAcceptSubmit = useCallback(
		async (notification: NotificationEdit): Promise<void> => {
			const response = await handleCreate(notification);
			if (isSuccess(response)) {
				await handleMutate();
				onModalClose();
				toast.success("通知設定を追加しました。");
			} else {
				toast.error("通知設定の追加に失敗しました。");
			}
		},
		[handleMutate, handleCreate, onModalClose]
	);
	if (
		session === null ||
		notifications === null ||
		statuses === null ||
		lineUserAccounts === null
	) {
		return null;
	}
	const displayStatuses: Status[] = getWithVendorStatuses(statuses);

	return (
		<>
			<NotificationList
				handleDestroy={handleDestroy}
				handleMutate={handleMutate}
				handleUpdate={handleUpdate}
				lineUserAccounts={lineUserAccounts.lineUserAccounts}
				notifications={notifications}
				statuses={displayStatuses}
				type="VendorNotification"
				useLineNotification={session.vendorCompany.useLineNotification || false}
			/>
			{isModalOpen ? (
				<NotificationModal
					handleAcceptSubmit={handleAcceptSubmit}
					handleClose={onModalClose}
					lineUserAccounts={lineUserAccounts.lineUserAccounts}
					statuses={displayStatuses}
					subtypes={null}
					type="VendorNotification"
					useLineNotification={
						session.vendorCompany.useLineNotification || false
					}
				/>
			) : null}
		</>
	);
};
